import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import TwoFactorSettings from '@/components/TwoFactorSettings';
import TeamManagement from '@/components/settings/TeamManagement';
import OrganisationManagement from '@/components/settings/OrganisationManagement';
import ComplianceDashboard from '@/components/settings/ComplianceDashboard';
import IntegrationSettings from '@/components/settings/IntegrationSettings';
import AccessRequestsManager from '@/components/settings/AccessRequestsManager';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  ArrowLeft, User, Shield, Bell, Palette, Lock, 
  Sun, Moon, Mail, Check, Eye, EyeOff, Sparkles,
  Monitor, Smartphone, Globe, LogOut, Users, Key, AlertTriangle,
  Building2, ClipboardCheck, Link as LinkIcon
} from 'lucide-react';

export default function Settings() {
  const navigate = useNavigate();
  const { user, token, organisation, loading: authLoading } = useAuth();
  const { theme, setThemeMode, isDark } = useTheme();
  
  // Profile state - initialize from user
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profileEmail, setProfileEmail] = useState(user?.email || '');
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    emailDigest: 'weekly',
    taskReminders: true,
    loginAlerts: true
  });
  const [savingNotifications, setSavingNotifications] = useState(false);

  // Weekly digest schedule (new, separate from legacy emailDigest)
  const [digestPrefs, setDigestPrefs] = useState({
    weeklyDigestEnabled: true,
    timezone: 'Australia/Sydney',
    lastWeeklyDigestSentAt: null,
  });
  const [savingDigestPrefs, setSavingDigestPrefs] = useState(false);

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showSignOutAllModal, setShowSignOutAllModal] = useState(false);

  // Access & Roles state (admin-only)
  const [accessSettings, setAccessSettings] = useState({
    defaultRole: 'viewer',
    restrictedHouseholdsRequireGrant: true,
    allowExportingClientPacks: false,
    allowViewingMaskedFields: false
  });
  const [savingAccessSettings, setSavingAccessSettings] = useState(false);
  const [loadingAccessSettings, setLoadingAccessSettings] = useState(false);

  // Active section - check URL params for default
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [activeSection, setActiveSection] = useState(tabParam || 'profile');
  
  // Check if user is admin
  const isAdmin = user?.role === 'org_admin' || user?.role === 'super_admin' || user?.role === 'owner' || user?.role === 'practice_manager' || user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (!authLoading && !token) {
      navigate('/login');
    }
  }, [authLoading, token, navigate]);

  useEffect(() => {
    const loadPreferences = async () => {
      if (!token) return;
      try {
        const response = await api.getUserPreferences(token);
        if (response.data) {
          setNotifications({
            emailDigest: response.data.email_digest || 'weekly',
            taskReminders: response.data.notifications_enabled !== false,
            loginAlerts: response.data.login_alerts !== false
          });
        }
      } catch (error) {
        console.error('Failed to load preferences:', error);
      }
      // Load the new weekly-digest preferences endpoint too
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me/preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const body = await res.json();
          setDigestPrefs({
            weeklyDigestEnabled: !!body.weeklyDigestEnabled,
            timezone: body.timezone || 'Australia/Sydney',
            lastWeeklyDigestSentAt: body.lastWeeklyDigestSentAt || null,
          });
        }
      } catch (e) {
        console.error('Failed to load digest prefs', e);
      }
    };
    loadPreferences();
  }, [token]);

  // Load sessions when admin switches to sessions tab
  useEffect(() => {
    const loadSessions = async () => {
      if (!token || !isAdmin || activeSection !== 'sessions') return;
      setLoadingSessions(true);
      try {
        const response = await api.getUserSessions(token);
        if (response.data?.sessions) {
          setSessions(response.data.sessions.map(s => ({
            ...s,
            lastActive: s.last_active
          })));
        }
      } catch (error) {
        console.error('Failed to load sessions:', error);
        // Set mock data as fallback
        setSessions([
          { id: '1', device: 'Current Device', browser: 'Current Browser', location: 'Unknown', lastActive: 'Now', current: true }
        ]);
      }
      setLoadingSessions(false);
    };
    loadSessions();
  }, [token, isAdmin, activeSection]);

  // Load access settings when admin switches to access tab
  useEffect(() => {
    const loadAccessSettings = async () => {
      if (!token || !isAdmin || activeSection !== 'access') return;
      setLoadingAccessSettings(true);
      try {
        const response = await api.getOrganisationAccessSettings(token);
        if (response.data) {
          setAccessSettings({
            defaultRole: response.data.default_role || 'viewer',
            restrictedHouseholdsRequireGrant: response.data.restricted_households_require_grant !== false,
            allowExportingClientPacks: response.data.allow_exporting_client_packs || false,
            allowViewingMaskedFields: response.data.allow_viewing_masked_fields || false
          });
        }
      } catch (error) {
        console.error('Failed to load access settings:', error);
      }
      setLoadingAccessSettings(false);
    };
    loadAccessSettings();
  }, [token, isAdmin, activeSection]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      // For now, just show success - would need backend endpoint
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    }
    setSavingProfile(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      // Would need backend endpoint for password change
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error('Failed to change password');
    }
    setChangingPassword(false);
  };

  const saveDigestPrefs = async (next) => {
    setSavingDigestPrefs(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me/preferences`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (!res.ok) throw new Error('Failed');
      setDigestPrefs((prev) => ({ ...prev, ...next }));
      toast.success('Digest preferences saved');
    } catch (e) {
      toast.error('Could not save preferences');
    } finally {
      setSavingDigestPrefs(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await api.updateUserPreferences(token, {
        email_digest: notifications.emailDigest,
        notifications_enabled: notifications.taskReminders,
        login_alerts: notifications.loginAlerts
      });
      toast.success('Notification preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    }
    setSavingNotifications(false);
  };

  const handleThemeChange = (newTheme) => {
    setThemeMode(newTheme, token);
    toast.success(`Switched to ${newTheme} mode`);
  };

  const handleSignOutSession = async (sessionId) => {
    try {
      await api.signOutSession(token, sessionId);
      setSessions(sessions.filter(s => s.id !== sessionId));
      toast.success('Session signed out successfully');
    } catch (error) {
      console.error('Failed to sign out session:', error);
      toast.error('Failed to sign out session');
    }
  };

  const handleSignOutAllSessions = async () => {
    try {
      await api.signOutAllSessions(token);
      setSessions(sessions.filter(s => s.current));
      setShowSignOutAllModal(false);
      toast.success('All other sessions have been signed out');
    } catch (error) {
      console.error('Failed to sign out all sessions:', error);
      toast.error('Failed to sign out sessions');
    }
  };

  const handleSaveAccessSettings = async () => {
    setSavingAccessSettings(true);
    try {
      await api.updateOrganisationAccessSettings(token, {
        default_role: accessSettings.defaultRole,
        restricted_households_require_grant: accessSettings.restrictedHouseholdsRequireGrant,
        allow_exporting_client_packs: accessSettings.allowExportingClientPacks,
        allow_viewing_masked_fields: accessSettings.allowViewingMaskedFields
      });
      toast.success('Access & role settings saved');
    } catch (error) {
      console.error('Failed to save access settings:', error);
      toast.error('Failed to save settings');
    }
    setSavingAccessSettings(false);
  };

  // Build sections array based on user role
  // Keep Settings personal — admin functions live in Business Admin
  const sections = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'integrations', label: 'Integrations', icon: LinkIcon },
    ...(isAdmin ? [
      { id: 'sessions', label: 'Sessions & Devices', icon: Monitor },
    ] : []),
    // Super Admin only
    ...(isSuperAdmin ? [
      { id: 'organisations', label: 'Organisations', icon: Building2 }
    ] : [])
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-primary)]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--text-secondary)]">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ backgroundColor: 'var(--bg-primary, #F9F7F5)' }} data-testid="settings-page">
      {/* Header */}
      <header className="border-b px-8 py-4 sticky top-0 z-40 transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }}>
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/app')}
            className="p-2"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--text-primary, #2C2420)' }} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl" style={{ color: 'var(--text-primary, #2C2420)' }}>Settings</h1>
            <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>Manage your account and preferences</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <nav className="w-56 flex-shrink-0">
            <div className="space-y-1">
              {sections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      isActive 
                        ? 'bg-[#4A3B32] text-white' 
                        : 'hover:bg-[var(--bg-hover,#F5F3F1)]'
                    }`}
                    style={{ color: isActive ? '#FFFFFF' : 'var(--text-primary, #2C2420)' }}
                    data-testid={`settings-nav-${section.id}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{section.label}</span>
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Content Area */}
          <div className="flex-1 min-w-0">
            {/* Profile Section */}
            {activeSection === 'profile' && (
              <div className="space-y-6" data-testid="settings-profile-section">
                <div className="rounded-xl p-6 border transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }}>
                  <h2 className="font-heading text-lg mb-6" style={{ color: 'var(--text-primary, #2C2420)' }}>Profile Information</h2>
                  
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 rounded-full bg-[#4A3B32] flex items-center justify-center">
                        <span className="text-white text-xl font-bold">
                          {user?.name?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium" style={{ color: 'var(--text-primary, #2C2420)' }}>{user?.name}</p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>{user?.role === 'super_admin' ? 'Super Admin' : user?.role === 'org_admin' ? 'Organisation Admin' : 'User'}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name" style={{ color: 'var(--text-primary, #2C2420)' }}>Full Name</Label>
                        <Input
                          id="name"
                          value={profileName}
                          onChange={(e) => setProfileName(e.target.value)}
                          className="caffeine-input"
                          data-testid="profile-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" style={{ color: 'var(--text-primary, #2C2420)' }}>Email Address</Label>
                        <Input
                          id="email"
                          type="email"
                          value={profileEmail}
                          onChange={(e) => setProfileEmail(e.target.value)}
                          className="caffeine-input"
                          disabled
                          data-testid="profile-email-input"
                        />
                        <p className="text-xs" style={{ color: 'var(--text-muted, #8C8078)' }}>Email cannot be changed</p>
                      </div>
                    </div>

                    {organisation && (
                      <div className="pt-4 border-t" style={{ borderColor: 'var(--border-color, #E6E0D9)' }}>
                        <Label style={{ color: 'var(--text-primary, #2C2420)' }}>Organisation</Label>
                        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary, #5D524D)' }}>{organisation.name}</p>
                      </div>
                    )}

                    <div className="pt-4 flex justify-end">
                      <Button
                        onClick={handleSaveProfile}
                        disabled={savingProfile}
                        className="caffeine-btn-primary"
                        data-testid="save-profile-btn"
                      >
                        {savingProfile ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Section */}
            {activeSection === 'security' && (
              <div className="space-y-6" data-testid="settings-security-section">
                {/* Two-Factor Authentication */}
                <TwoFactorSettings />

                {/* Change Password */}
                <div className="rounded-xl p-6 border transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--bg-hover, #F5F3F1)' }}>
                      <Lock className="w-5 h-5" style={{ color: 'var(--text-primary, #4A3B32)' }} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg" style={{ color: 'var(--text-primary, #2C2420)' }}>Change Password</h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>Update your account password</p>
                    </div>
                  </div>

                  <form onSubmit={handleChangePassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="current-password" style={{ color: 'var(--text-primary, #2C2420)' }}>Current Password</Label>
                      <div className="relative">
                        <Input
                          id="current-password"
                          type={showPasswords ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="caffeine-input pr-10"
                          data-testid="current-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(!showPasswords)}
                          className="absolute right-3 top-1/2 -translate-y-1/2"
                          style={{ color: 'var(--text-muted, #8C8078)' }}
                        >
                          {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="new-password" style={{ color: 'var(--text-primary, #2C2420)' }}>New Password</Label>
                        <Input
                          id="new-password"
                          type={showPasswords ? 'text' : 'password'}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="caffeine-input"
                          minLength={8}
                          data-testid="new-password-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirm-new-password" style={{ color: 'var(--text-primary, #2C2420)' }}>Confirm New Password</Label>
                        <Input
                          id="confirm-new-password"
                          type={showPasswords ? 'text' : 'password'}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="caffeine-input"
                          data-testid="confirm-new-password-input"
                        />
                      </div>
                    </div>

                    <p className="text-xs" style={{ color: 'var(--text-muted, #8C8078)' }}>
                      Password must be at least 8 characters long
                    </p>

                    <div className="pt-2 flex justify-end">
                      <Button
                        type="submit"
                        disabled={changingPassword || !currentPassword || !newPassword}
                        className="caffeine-btn-primary"
                        data-testid="change-password-btn"
                      >
                        {changingPassword ? 'Changing...' : 'Change Password'}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Notifications Section */}
            {activeSection === 'notifications' && (
              <div className="space-y-6" data-testid="settings-notifications-section">
                <div className="rounded-xl p-6 border transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--bg-hover, #F5F3F1)' }}>
                      <Mail className="w-5 h-5" style={{ color: 'var(--text-primary, #4A3B32)' }} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg" style={{ color: 'var(--text-primary, #2C2420)' }}>Email Notifications</h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>Manage your email preferences</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Email Digest Frequency */}
                    <div className="space-y-3">
                      <Label style={{ color: 'var(--text-primary, #2C2420)' }}>Task Digest Frequency</Label>
                      <div className="flex gap-3">
                        {['daily', 'weekly', 'none'].map((option) => (
                          <button
                            key={option}
                            onClick={() => setNotifications({ ...notifications, emailDigest: option })}
                            className={`px-4 py-2 rounded-lg border transition-colors ${
                              notifications.emailDigest === option
                                ? 'bg-[#4A3B32] text-white border-[#4A3B32]'
                                : 'border-[var(--border-color,#E6E0D9)] hover:bg-[var(--bg-hover,#F5F3F1)]'
                            }`}
                            style={{ color: notifications.emailDigest === option ? '#FFFFFF' : 'var(--text-primary, #2C2420)' }}
                            data-testid={`digest-${option}-btn`}
                          >
                            {option.charAt(0).toUpperCase() + option.slice(1)}
                          </button>
                        ))}
                      </div>
                      <p className="text-xs" style={{ color: 'var(--text-muted, #8C8078)' }}>
                        Receive a summary of your upcoming and overdue tasks
                      </p>
                    </div>

                    {/* Task Reminders Toggle */}
                    <div className="flex items-center justify-between py-4 border-t" style={{ borderColor: 'var(--border-color, #E6E0D9)' }}>
                      <div>
                        <p className="font-medium" style={{ color: 'var(--text-primary, #2C2420)' }}>Task Reminders</p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>Get notified about upcoming task deadlines</p>
                      </div>
                      <button
                        onClick={() => setNotifications({ ...notifications, taskReminders: !notifications.taskReminders })}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          notifications.taskReminders ? 'bg-[#6B8E6B]' : 'bg-[var(--border-color,#D9D6D0)]'
                        }`}
                        data-testid="task-reminders-toggle"
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          notifications.taskReminders ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>

                    {/* Login Alerts Toggle */}
                    <div className="flex items-center justify-between py-4 border-t" style={{ borderColor: 'var(--border-color, #E6E0D9)' }}>
                      <div>
                        <p className="font-medium" style={{ color: 'var(--text-primary, #2C2420)' }}>Login Alerts</p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>Get notified when someone signs into your account</p>
                      </div>
                      <button
                        onClick={() => setNotifications({ ...notifications, loginAlerts: !notifications.loginAlerts })}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          notifications.loginAlerts ? 'bg-[#6B8E6B]' : 'bg-[var(--border-color,#D9D6D0)]'
                        }`}
                        data-testid="login-alerts-toggle"
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          notifications.loginAlerts ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>

                    <div className="pt-4 flex justify-end">
                      <Button
                        onClick={handleSaveNotifications}
                        disabled={savingNotifications}
                        className="caffeine-btn-primary"
                        data-testid="save-notifications-btn"
                      >
                        {savingNotifications ? 'Saving...' : 'Save Preferences'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Weekly Digest Schedule (Resend-backed, Monday 8 AM local) */}
                <div className="rounded-xl p-6 border transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }} data-testid="weekly-digest-prefs-card">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--bg-hover, #F5F3F1)' }}>
                      <Mail className="w-5 h-5" style={{ color: 'var(--text-primary, #4A3B32)' }} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg" style={{ color: 'var(--text-primary, #2C2420)' }}>Weekly Digest Email</h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>Delivered Monday at 8&nbsp;AM in your local timezone</p>
                    </div>
                  </div>

                  {/* Toggle */}
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="font-medium" style={{ color: 'var(--text-primary, #2C2420)' }}>Email me the weekly digest</p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>
                        Tasks · reviews · new leads · handover notes for the week
                      </p>
                    </div>
                    <button
                      onClick={() => saveDigestPrefs({ weeklyDigestEnabled: !digestPrefs.weeklyDigestEnabled })}
                      disabled={savingDigestPrefs}
                      className={`w-12 h-6 rounded-full transition-colors disabled:opacity-50 ${
                        digestPrefs.weeklyDigestEnabled ? 'bg-[#6B8E6B]' : 'bg-[var(--border-color,#D9D6D0)]'
                      }`}
                      data-testid="weekly-digest-toggle"
                      aria-label="Toggle weekly digest"
                    >
                      <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                        digestPrefs.weeklyDigestEnabled ? 'translate-x-6' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  {/* Timezone select */}
                  <div className="py-4 border-t" style={{ borderColor: 'var(--border-color, #E6E0D9)' }}>
                    <Label className="block mb-2" style={{ color: 'var(--text-primary, #2C2420)' }}>Your timezone</Label>
                    <select
                      value={digestPrefs.timezone}
                      onChange={(e) => saveDigestPrefs({ timezone: e.target.value })}
                      disabled={savingDigestPrefs || !digestPrefs.weeklyDigestEnabled}
                      data-testid="weekly-digest-timezone-select"
                      className="w-full sm:w-80 px-3 py-2 rounded-lg border bg-white disabled:opacity-60"
                      style={{ borderColor: 'var(--border-color, #E6E0D9)', color: 'var(--text-primary, #2C2420)' }}
                    >
                      {[
                        'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane',
                        'Australia/Adelaide', 'Australia/Perth', 'Australia/Hobart',
                        'Australia/Darwin', 'Pacific/Auckland',
                        'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo',
                        'Europe/London', 'America/New_York', 'America/Los_Angeles', 'UTC',
                      ].map((tz) => <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>)}
                    </select>
                    <p className="text-xs mt-2" style={{ color: 'var(--text-muted, #8C8078)' }}>
                      Used to time the Monday 8&nbsp;AM delivery.
                      {digestPrefs.lastWeeklyDigestSentAt && (
                        <> · Last sent: {new Date(digestPrefs.lastWeeklyDigestSentAt).toLocaleString()}</>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Appearance Section */}
            {activeSection === 'appearance' && (
              <div className="space-y-6" data-testid="settings-appearance-section">
                <div className="rounded-xl p-6 border transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--bg-hover, #F5F3F1)' }}>
                      <Palette className="w-5 h-5" style={{ color: 'var(--text-primary, #4A3B32)' }} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg" style={{ color: 'var(--text-primary, #2C2420)' }}>Theme</h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>Choose your preferred color scheme</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Light Mode Card */}
                    <button
                      onClick={() => handleThemeChange('light')}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        theme === 'light' 
                          ? 'border-[#4A3B32] ring-2 ring-[#4A3B32]/20' 
                          : 'border-[var(--border-color,#E6E0D9)] hover:border-[#4A3B32]/50'
                      }`}
                      data-testid="theme-light-btn"
                    >
                      <div className="bg-[#F9F7F5] rounded-lg p-4 mb-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-3 h-3 rounded-full bg-[#4A3B32]" />
                          <div className="h-2 bg-[#E6E0D9] rounded flex-1" />
                        </div>
                        <div className="space-y-1">
                          <div className="h-2 bg-[#E6E0D9] rounded w-3/4" />
                          <div className="h-2 bg-[#E6E0D9] rounded w-1/2" />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sun className="w-4 h-4" style={{ color: 'var(--text-primary, #2C2420)' }} />
                          <span className="font-medium" style={{ color: 'var(--text-primary, #2C2420)' }}>Light</span>
                        </div>
                        {theme === 'light' && (
                          <div className="w-5 h-5 rounded-full bg-[#6B8E6B] flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Dark Mode Card */}
                    <button
                      onClick={() => handleThemeChange('dark')}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        theme === 'dark' 
                          ? 'border-[#D99A6C] ring-2 ring-[#D99A6C]/20' 
                          : 'border-[var(--border-color,#E6E0D9)] hover:border-[#D99A6C]/50'
                      }`}
                      data-testid="theme-dark-btn"
                    >
                      <div className="bg-[#1A1614] rounded-lg p-4 mb-3">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-3 h-3 rounded-full bg-[#D99A6C]" />
                          <div className="h-2 bg-[#3D3935] rounded flex-1" />
                        </div>
                        <div className="space-y-1">
                          <div className="h-2 bg-[#3D3935] rounded w-3/4" />
                          <div className="h-2 bg-[#3D3935] rounded w-1/2" />
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Moon className="w-4 h-4" style={{ color: 'var(--text-primary, #2C2420)' }} />
                          <span className="font-medium" style={{ color: 'var(--text-primary, #2C2420)' }}>Dark</span>
                        </div>
                        {theme === 'dark' && (
                          <div className="w-5 h-5 rounded-full bg-[#6B8E6B] flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </div>
                    </button>
                  </div>

                  <p className="text-sm mt-4" style={{ color: 'var(--text-muted, #8C8078)' }}>
                    Your theme preference is automatically synced across all your devices.
                  </p>
                </div>

                {/* Restart Tour */}
                <div className="rounded-xl p-6 border transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--bg-hover, #F5F3F1)' }}>
                      <Sparkles className="w-5 h-5" style={{ color: 'var(--text-primary, #4A3B32)' }} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg" style={{ color: 'var(--text-primary, #2C2420)' }}>Product Tour</h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>Learn how to use QIndex One</p>
                    </div>
                  </div>
                  
                  <p className="text-sm mb-4" style={{ color: 'var(--text-secondary, #5D524D)' }}>
                    Take a guided tour of the key features in your Client 360 dashboard.
                  </p>

                  <Button
                    onClick={async () => {
                      try {
                        await api.updateUserPreferences(token, { onboarding_completed: false });
                        toast.success('Tour reset! Go to the Dashboard to start.');
                        navigate('/app');
                      } catch (error) {
                        toast.error('Failed to reset tour');
                      }
                    }}
                    variant="outline"
                    className="w-full caffeine-btn-outline"
                    data-testid="restart-tour-btn"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Restart Product Tour
                  </Button>
                </div>
              </div>
            )}

            {/* Team Section (Admin Only) */}
            {activeSection === 'team' && isAdmin && (
              <div className="rounded-xl p-6 border transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }} data-testid="settings-team-section">
                <TeamManagement />
              </div>
            )}

            {/* Access Requests Section (Admin Only) */}
            {activeSection === 'access-requests' && isAdmin && (
              <div className="rounded-xl p-6 border transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }} data-testid="settings-access-requests-section">
                <AccessRequestsManager />
              </div>
            )}

            {/* Sessions & Devices Section (Admin Only) */}
            {activeSection === 'sessions' && isAdmin && (
              <div className="space-y-6" data-testid="settings-sessions-section">
                <div className="rounded-xl p-6 border transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--bg-hover, #F5F3F1)' }}>
                        <Monitor className="w-5 h-5" style={{ color: 'var(--text-primary, #4A3B32)' }} />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg" style={{ color: 'var(--text-primary, #2C2420)' }}>Sessions & Devices</h3>
                        <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>Manage your active sessions across devices</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setShowSignOutAllModal(true)}
                      className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                      data-testid="sign-out-all-btn"
                      disabled={sessions.length <= 1}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign out all sessions
                    </Button>
                  </div>

                  {/* Sessions Table */}
                  {loadingSessions ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                  <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--border-color, #E6E0D9)' }}>
                    <table className="w-full">
                      <thead>
                        <tr style={{ backgroundColor: 'var(--bg-hover, #F5F3F1)' }}>
                          <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: 'var(--text-secondary, #5D524D)' }}>Device</th>
                          <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: 'var(--text-secondary, #5D524D)' }}>Browser</th>
                          <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: 'var(--text-secondary, #5D524D)' }}>Approx. Location</th>
                          <th className="px-4 py-3 text-left text-sm font-medium" style={{ color: 'var(--text-secondary, #5D524D)' }}>Last Active</th>
                          <th className="px-4 py-3 text-right text-sm font-medium" style={{ color: 'var(--text-secondary, #5D524D)' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessions.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--text-muted, #8C8078)' }}>
                              No active sessions found
                            </td>
                          </tr>
                        ) : sessions.map((session) => (
                          <tr 
                            key={session.id} 
                            className="border-t" 
                            style={{ borderColor: 'var(--border-color, #E6E0D9)' }}
                            data-testid={`session-row-${session.id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                {session.device.includes('iPhone') || session.device.includes('Android') ? (
                                  <Smartphone className="w-4 h-4" style={{ color: 'var(--text-muted, #8C8078)' }} />
                                ) : (
                                  <Monitor className="w-4 h-4" style={{ color: 'var(--text-muted, #8C8078)' }} />
                                )}
                                <span className="font-medium" style={{ color: 'var(--text-primary, #2C2420)' }}>
                                  {session.device}
                                </span>
                                {session.current && (
                                  <span className="px-2 py-0.5 text-xs rounded-full bg-[#6B8E6B]/10 text-[#6B8E6B]">
                                    Current
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>
                              {session.browser}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-1 text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>
                                <Globe className="w-3 h-3" />
                                {session.location}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>
                              {session.lastActive}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {!session.current && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleSignOutSession(session.id)}
                                  className="text-red-600 hover:bg-red-50"
                                  data-testid={`sign-out-session-${session.id}`}
                                >
                                  Sign out
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  )}

                  {/* Security Note */}
                  <div className="mt-4 p-3 rounded-lg flex items-start gap-2" style={{ backgroundColor: 'var(--bg-hover, #F5F3F1)' }}>
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
                    <p className="text-xs" style={{ color: 'var(--text-secondary, #5D524D)' }}>
                      <strong>Security tip:</strong> Review your active sessions regularly. If you see any unfamiliar devices or locations, 
                      sign out of those sessions immediately and consider changing your password.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Access & Roles Section (Admin Only) */}
            {activeSection === 'access' && isAdmin && (
              <div className="space-y-6" data-testid="settings-access-section">
                <div className="rounded-xl p-6 border transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--bg-hover, #F5F3F1)' }}>
                      <Key className="w-5 h-5" style={{ color: 'var(--text-primary, #4A3B32)' }} />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg" style={{ color: 'var(--text-primary, #2C2420)' }}>Access & Roles</h3>
                      <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>Configure default permissions and access controls</p>
                    </div>
                  </div>

                  {loadingAccessSettings ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-2 border-[#4A3B32] border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                  <div className="space-y-6">
                    {/* Default Role for New Users */}
                    <div className="space-y-2">
                      <Label style={{ color: 'var(--text-primary, #2C2420)' }}>Default role for new users</Label>
                      <Select
                        value={accessSettings.defaultRole}
                        onValueChange={(value) => setAccessSettings({ ...accessSettings, defaultRole: value })}
                      >
                        <SelectTrigger className="w-full max-w-xs caffeine-input" data-testid="default-role-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="viewer">Viewer (Read-only access)</SelectItem>
                          <SelectItem value="editor">Editor (Can modify records)</SelectItem>
                          <SelectItem value="admin">Admin (Full access)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs" style={{ color: 'var(--text-muted, #8C8078)' }}>
                        This role will be assigned to new team members when they join the organisation
                      </p>
                    </div>

                    {/* Restricted Households Toggle */}
                    <div className="flex items-center justify-between py-4 border-t" style={{ borderColor: 'var(--border-color, #E6E0D9)' }}>
                      <div>
                        <p className="font-medium" style={{ color: 'var(--text-primary, #2C2420)' }}>Restricted households require explicit grant</p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>
                          Users must be explicitly granted access to view restricted/confidential households
                        </p>
                      </div>
                      <button
                        onClick={() => setAccessSettings({ ...accessSettings, restrictedHouseholdsRequireGrant: !accessSettings.restrictedHouseholdsRequireGrant })}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          accessSettings.restrictedHouseholdsRequireGrant ? 'bg-[#6B8E6B]' : 'bg-[var(--border-color,#D9D6D0)]'
                        }`}
                        data-testid="restricted-households-toggle"
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          accessSettings.restrictedHouseholdsRequireGrant ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>

                    {/* Allow Exporting Client Packs Toggle */}
                    <div className="flex items-center justify-between py-4 border-t" style={{ borderColor: 'var(--border-color, #E6E0D9)' }}>
                      <div>
                        <p className="font-medium" style={{ color: 'var(--text-primary, #2C2420)' }}>Allow exporting client packs</p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>
                          Enable users to download client data as PDF or CSV exports
                        </p>
                      </div>
                      <button
                        onClick={() => setAccessSettings({ ...accessSettings, allowExportingClientPacks: !accessSettings.allowExportingClientPacks })}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          accessSettings.allowExportingClientPacks ? 'bg-[#6B8E6B]' : 'bg-[var(--border-color,#D9D6D0)]'
                        }`}
                        data-testid="allow-export-toggle"
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          accessSettings.allowExportingClientPacks ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>

                    {/* Allow Viewing Masked Fields Toggle */}
                    <div className="flex items-center justify-between py-4 border-t" style={{ borderColor: 'var(--border-color, #E6E0D9)' }}>
                      <div>
                        <p className="font-medium" style={{ color: 'var(--text-primary, #2C2420)' }}>Allow viewing masked fields (requires permission)</p>
                        <p className="text-sm" style={{ color: 'var(--text-secondary, #5D524D)' }}>
                          Permit users with elevated access to view sensitive masked data (DOB, TFN, etc.)
                        </p>
                      </div>
                      <button
                        onClick={() => setAccessSettings({ ...accessSettings, allowViewingMaskedFields: !accessSettings.allowViewingMaskedFields })}
                        className={`w-12 h-6 rounded-full transition-colors ${
                          accessSettings.allowViewingMaskedFields ? 'bg-[#6B8E6B]' : 'bg-[var(--border-color,#D9D6D0)]'
                        }`}
                        data-testid="allow-masked-fields-toggle"
                      >
                        <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                          accessSettings.allowViewingMaskedFields ? 'translate-x-6' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>

                    {/* Admin Role Information */}
                    <div className="p-4 rounded-lg border" style={{ borderColor: 'var(--border-color, #E6E0D9)', backgroundColor: 'var(--bg-hover, #F5F3F1)' }}>
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-4 h-4" style={{ color: 'var(--text-primary, #4A3B32)' }} />
                        <h4 className="font-medium text-sm" style={{ color: 'var(--text-primary, #2C2420)' }}>Organisation Role Hierarchy</h4>
                      </div>
                      <div className="space-y-1 text-xs" style={{ color: 'var(--text-secondary, #5D524D)' }}>
                        <p><strong>Owner</strong> — Full administrative access, can manage all settings and users</p>
                        <p><strong>Practice Manager</strong> — Full access to client data, can grant access to specific clients</p>
                        <p><strong>General User</strong> — Access to assigned clients only, based on granted permissions</p>
                      </div>
                    </div>

                    <div className="pt-4 flex justify-end">
                      <Button
                        onClick={handleSaveAccessSettings}
                        disabled={savingAccessSettings}
                        className="caffeine-btn-primary"
                        data-testid="save-access-settings-btn"
                      >
                        {savingAccessSettings ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            )}

            {/* Compliance Section (Admin Only) */}
            {activeSection === 'compliance' && isAdmin && (
              <ComplianceDashboard />
            )}

            {/* Integrations Section */}
            {activeSection === 'integrations' && (
              <div className="rounded-xl p-6 border transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }} data-testid="settings-integrations-section">
                <IntegrationSettings />
              </div>
            )}

            {/* Organisations Section (Super Admin Only) */}
            {activeSection === 'organisations' && isSuperAdmin && (
              <div className="rounded-xl p-6 border transition-colors duration-300" style={{ backgroundColor: 'var(--bg-card, #FFFFFF)', borderColor: 'var(--border-color, #E6E0D9)' }} data-testid="settings-organisations-section">
                <OrganisationManagement />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Sign Out All Sessions Confirmation Modal */}
      <AlertDialog open={showSignOutAllModal} onOpenChange={setShowSignOutAllModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out of all sessions?</AlertDialogTitle>
            <AlertDialogDescription>
              This will sign you out of all devices except your current session. You'll need to sign in again on those devices.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSignOutAllSessions}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-sign-out-all-btn"
            >
              Sign out all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
